import { useCallback, useEffect, useState } from 'react'
import { PaletteMode } from '@mui/material'

export interface LocalStorage {
  theme: PaletteMode
}

declare global {
  interface WindowEventMap {
    'local-storage': CustomEvent
  }
}

function getStorageValue(key: string, defaultValue: any) {
  const saved = localStorage.getItem(key)
  const initial = saved ? JSON.parse(saved) : saved
  return initial || defaultValue
}

export function useLocalStorage<T extends keyof LocalStorage>(
  key: T,
  initialValue?: LocalStorage[T]
): [LocalStorage[T], (value: LocalStorage[T]) => void] {
  const [value, setValue] = useState(getStorageValue(key, initialValue))

  const handleStorageChange = useCallback(() => {
    setValue(getStorageValue(key, initialValue))
  }, [key, initialValue])

  useEffect(() => {
    window.addEventListener('local-storage', handleStorageChange)
    return () => {
      window.removeEventListener('local-storage', handleStorageChange)
    }
  }, [])

  useEffect(() => {
    localStorage.setItem(key, JSON.stringify(value))
    window.dispatchEvent(new StorageEvent('local-storage', { key }))
  }, [key, value])

  return [value, setValue]
}
